import React, { useState } from 'react';
import { useParams } from 'react-router';
import {
  Button,
  Highlight,
  IconBannersWarning,
  LoadingSpinner,
  VStack,
} from '@adc/parallax-component-library';
import i18n from '../../utilities/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deviceByKeyQueryOptions,
  deviceCapabilitiesQueryOptions,
  deviceOverridesByDeviceKeyQueryOptions,
  updateDeviceBlockStatusMutateOptions,
} from '../../queryOptions';
import { useErrorMessageOnEntityView } from '../../hooks/useErrorMessageOnEntityView';
import {
  useResolvedDeviceHierarchy,
  useSyncCurrentOrganization,
} from '../../hooks';
import DevicePageHeader from './deviceComponents/DeviceHeader';
import { ConfirmationModal } from '../modal/ConfirmationModal';
import { DeviceStatus } from 'adc-sirius-module-common';

/**
 * Device component that displays the details of a specific device.
 * @returns {React.JSX.Element}
 */
export default function Device() {
  const queryClient = useQueryClient();
  const { deviceKey } = useParams();

  const { data: capabilities } = useQuery(
    deviceCapabilitiesQueryOptions({ key: deviceKey }),
  );

  const device = useQuery(
    deviceByKeyQueryOptions(deviceKey || '', {
      enabled: !!deviceKey,
    }),
  );

  const deviceOverrides = useQuery(
    deviceOverridesByDeviceKeyQueryOptions(deviceKey || '', {
      enabled: !!deviceKey,
    }),
  );

  const { subject, site, study, organization, organizationKey } =
    useResolvedDeviceHierarchy(device.data);

  useSyncCurrentOrganization(organizationKey);

  const hierarchyQueries = [subject, site, study, organization];
  const isHierarchyLoading = hierarchyQueries.some((query) => query.isLoading);
  const isHierarchyError = hierarchyQueries.some((query) => query.isError);

  const updateBlockStatus = useMutation(
    updateDeviceBlockStatusMutateOptions({
      onSuccess: (updatedDevice) => {
        queryClient.setQueryData(
          deviceByKeyQueryOptions(updatedDevice.key).queryKey,
          updatedDevice,
        );
      },
    }),
  );

  const loadErrorMessage = useErrorMessageOnEntityView(
    device.error ||
      deviceOverrides.error ||
      hierarchyQueries.find((query) => query.isError)?.error,
    'Page_Device.error_load',
  );

  const actionErrorMessage = useErrorMessageOnEntityView(
    updateBlockStatus.error,
    'Page_Device.error_generic',
  );

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const isDeviceBlocked = device.data?.status === DeviceStatus.Blocked;

  const handleConfirm = () => {
    if (!device.data) {
      console.error(
        'Device data is not available for block/unblock operation.',
      );

      return;
    }

    return updateBlockStatus
      .mutateAsync({
        deviceKey: device.data.key,
        updatedStatus: isDeviceBlocked
          ? DeviceStatus.Active
          : DeviceStatus.Blocked,
      })
      .catch(() => undefined)
      .finally(() => {
        setIsConfirmationModalOpen(false);
      });
  };

  const handleCancel = () => {
    setIsConfirmationModalOpen(false);
  };

  return (
    <VStack gap="$2" height="100%">
      {loadErrorMessage ? (
        <Highlight
          variant="danger"
          iconStart={
            <IconBannersWarning
              color="$neutral.0"
              accentColor="$support.danger.default"
            />
          }
          title={i18n.t('Page_Device.error_load_title')}
          body={i18n.t(loadErrorMessage)}
          testID="device-error-banner"
        />
      ) : null}

      {actionErrorMessage ? (
        <Highlight
          variant="danger"
          iconStart={
            <IconBannersWarning
              color="$neutral.0"
              accentColor="$support.danger.default"
            />
          }
          title={i18n.t('Page_Device.error_generic_title')}
          body={i18n.t(actionErrorMessage)}
          testID="device-action-error-banner"
        />
      ) : null}

      {device.isPending || deviceOverrides.isPending || isHierarchyLoading ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="device-loading" />
        </VStack>
      ) : null}

      {device.isSuccess &&
      deviceOverrides.isSuccess &&
      !isHierarchyLoading &&
      !isHierarchyError ? (
        <VStack gap="$4">
          <DevicePageHeader
            device={device.data}
            deviceOverrides={deviceOverrides.data}
            resolvedHierarchyNames={{
              organizationName: organization.data?.name,
              studyName: study.data?.name,
              siteName: site.data?.name,
              subjectName: subject.data?.name,
            }}
          >
            {capabilities?.canLockDevice ? (
              <Button
                variant="secondary"
                size="medium"
                onPress={() => {
                  setIsConfirmationModalOpen(true);
                }}
                testID="device-block-unblock-button"
                label={
                  isDeviceBlocked
                    ? i18n.t('Page_Device.button_unblock')
                    : i18n.t('Page_Device.button_block')
                }
              />
            ) : null}
          </DevicePageHeader>
        </VStack>
      ) : null}

      <ConfirmationModal
        title={i18n.t('Page_Device.confirmation_modal_title')}
        description={
          isDeviceBlocked
            ? i18n.t('Page_Device.confirmation_modal_unblock_description')
            : i18n.t('Page_Device.confirmation_modal_block_description')
        }
        isOpen={isConfirmationModalOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        showCancelButton={true}
      />
    </VStack>
  );
}
